import { Form, Input, Button, AutoComplete, Alert } from 'antd';
import { LoadingOutlined, DownOutlined, InfoCircleFilled } from '@ant-design/icons';
import '../index.css';
import React from 'react';
import { Captcha } from '../../components/captcha/index';
import { IResultSet, enmValidationLevel } from '../../components/infrastructure/models';
import { Core } from '../../components/infrastructure/core';
import TextArea from 'antd/lib/input/TextArea';
import debounce from 'lodash/debounce';
import { Timer } from '../forgetPassword/timer';
import { CustomJalaliDatePicker } from '../../components/jalaliDatePicker/customJalaliDatePicker';

//const { Option } = AutoComplete;

interface IRegisterPolicyholderState {
    loading?: boolean;
}

interface IRegisterPolicyholderProp {
    nationalCode: string;
    onReturn: () => void;
    onRegistered: (userName: string, phone: string, captcha: string, isNew: boolean) => void;
}

interface IProvince {
    Name: string;
    Code: string;
}

interface ICity {
    Name: string;
    Code: string;
    ProvinceCode: string;
}

interface IProvinceStates {
    loaded: boolean;
    selectedProvinceCode?: string;
    originalProvinceList: IProvince[];
    provinceList: IProvince[];
}

interface ICityStates {
    loading: boolean;
    selectedCityCode?: string;
    cityList: ICity[];
}

interface IPolicyholderState {
    birthDate?: string;
    disabled: boolean;
    loading: boolean;
    policyholderData?: any;
}

const RegisterPolicyholder = (props: IRegisterPolicyholderProp) => {
    const [state, setState] = React.useState<IRegisterPolicyholderState>({});
    const [policyholderState, setPolicyholderState] = React.useState<IPolicyholderState>({ loading: false, disabled: true });
    const [provinceState, setProvinceState] = React.useState<IProvinceStates>({ loaded: false, originalProvinceList: [], provinceList: [] });
    const [cityState, setCityState] = React.useState<ICityStates>({ loading: false, cityList: [] });
    const [form] = Form.useForm();

    const componentWillUnmount = React.useRef(false);
    const policyholderLoading = React.useRef(false);

    const register = async (values: any) => {
        if (state.loading)
            return;

        setState({ ...state, loading: true });

        let response = await fetch("api/userpolicyholder/registerpolicyholder", {
            method: 'POST',
            body: JSON.stringify({
                policyholder: {
                    NationalCode: props.nationalCode,
                    BirthDate: policyholderState.birthDate,
                    FirstName: values["firstName"],
                    LastName: values["lastName"],
                    FatherName: values["fatherName"],
                    Phone: values["phone"],
                    Email: values["email"],
                    Address: values["address"],
                    PostalCode: values["postalCode"],
                    ProvinceCode: provinceState.selectedProvinceCode,
                    CityCode: cityState.selectedCityCode
                },
                captcha: values['captcha']
            }),
            headers: { 'content-type': 'application/json;charset=UTF-8' }
        });

        if (componentWillUnmount.current === true)
            return;

        let result: IResultSet<any> = await response.json();

        if (!result.ValidationState.Successful) {
            if (result.ValidationState.ValidationResults.find(v => [enmValidationLevel.Unauthorized, enmValidationLevel.AccessDenied].indexOf(v.Level) > -1)) {
                Core.errorNotification(result.ValidationState.ValidationResults[0].Message!);
                props.onReturn();
                return;
            }
            Core.errorNotification(result.ValidationState.ValidationResults[0].Message);
            setState({ ...state, loading: false });
        }
        else {
            if (result.ValidationState.ValidationResults.length) {
                Core.successNotification(result.ValidationState.ValidationResults[0].Message);
                setState({ ...state, loading: false });
            }
            else {
                const { UserName, Phone, IsNew } = result.Data;
                if (IsNew)
                    Core.successNotification("اطلاعات با موفقیت ثبت شد، لطفا سریال یا IMEI دستگاه موبایل را استعلام نمایید");
                props.onRegistered(UserName, Phone, IsNew ? values['captcha'] : '', IsNew);
            }
        }
    };

    const getProvinceList = async () => {
        if (state.loading)
            return;

        setState({ ...state, loading: true });

        let response = await fetch("api/userpolicyholder/getprovincelist", {
            method: 'POST',
            headers: { 'content-type': 'application/json;charset=UTF-8' }
        });

        if (componentWillUnmount.current === true)
            return;

        let result: IResultSet<any> = await response.json();
        if (!result.ValidationState.Successful) {
            Core.errorNotification(result.ValidationState.ValidationResults[0].Message);
            setProvinceState({ ...provinceState, loaded: true });
        }
        else {
            setProvinceState({ loaded: true, originalProvinceList: result.Data, provinceList: result.Data });
        }
        setState({ ...state, loading: false });
    };

    const getCityList = async (provinceCode?: string, value?: string) => {
        if (cityState.loading)
            return;

        setCityState({ ...cityState, loading: true });

        let response = await fetch("api/userpolicyholder/getcitylist", {
            method: 'POST',
            body: JSON.stringify({ provinceCode, value }),
            headers: { 'content-type': 'application/json;charset=UTF-8' }
        });

        if (componentWillUnmount.current === true)
            return;

        let result: IResultSet<any> = await response.json();
        if (!result.ValidationState.Successful) {
            Core.errorNotification(result.ValidationState.ValidationResults[0].Message);
            setCityState({ ...cityState, loading: false });
        }
        else {
            setCityState({ loading: false, cityList: result.Data });
        }
    };

    const debouncedCitySearch = React.useMemo(() => {
        return debounce(getCityList, 700);
    }, []);

    const back = async () => {
        let response = await fetch("api/userpolicyholder/logoutpolicyholderextraaccess", {
            method: 'POST',
            headers: { 'content-type': 'application/json;charset=UTF-8' }
        });
        await response.json();
        props.onReturn();
    };

    const checkPolicyholder = async (values: any) => {
        if (policyholderState.loading)
            return;

        setPolicyholderState({ ...policyholderState, loading: true });

        let response = await fetch("api/userpolicyholder/checkpolicyholder", {
            method: 'POST',
            body: JSON.stringify({
                nationalCode: props.nationalCode,
                birthDate: policyholderState.birthDate,
                captcha: values['captcha']
            }),
            headers: { 'content-type': 'application/json;charset=UTF-8' }
        });

        if (componentWillUnmount.current || !policyholderLoading.current) {
            return;
        }

        let result: IResultSet<any> = await response.json();

        if (!result.ValidationState.Successful) {
            Core.errorNotification(result.ValidationState.ValidationResults[0].Message);
        }
        else if (result.ValidationState.ValidationResults.length) {
            Core.successNotification(result.ValidationState.ValidationResults[0].Message);
        }

        setPolicyholderState({ ...policyholderState, loading: false, disabled: !result.Data && !result.ValidationState.Successful, policyholderData: result.Data });

        if (result.ValidationState.Successful) {
            form.setFieldsValue(result.Data);
        }
    };

    const finish = (values: any) => {
        if (policyholderState.disabled) {
            checkPolicyholder(values);
        }
        else
            register(values);
    }

    React.useEffect(() => {
        return () => {
            componentWillUnmount.current = true;
            debouncedCitySearch.cancel();
            //debouncedEquipmentSearch.cancel();
            //debouncedBrandSearch.cancel();
            //debouncedEquipmentModelSearch.cancel();
        };
    }, []);

    React.useEffect(() => {
        policyholderLoading.current = policyholderState.loading;
    }, [policyholderState.loading]);

    React.useEffect(() => {
        if (!policyholderState.policyholderData)
            form.resetFields(['firstName', 'lastName', 'fatherName', 'address', 'postalCode', 'city', 'province', 'phone', 'email']);
        else
            form.setFieldsValue(policyholderState.policyholderData);

        return () => {

        };
    }, [policyholderState.policyholderData]);

    if (!provinceState.loaded && !policyholderState.disabled) {
        getProvinceList();
    }

    return (
        <Form
            scrollToFirstError
            form={form}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            name="normal_login"
            className=" login-form col-sm-8 col-md-6 col-lg-6 col-xl-4 col-xs-8 "
            initialValues={{ remember: true }}
            onFinish={finish}
            onReset={back}
            style={{ width: "calc(100% - 20px)", height: "100%", textAlign: 'right', margin: 10 }}
        >
            <div className="box box-blue" style={{ backgroundColor: "#fefefe90", border: "1px solid white", borderRadius: 15 }}>
                <div className="box-title" style={{ border: "1px solid white", borderTopLeftRadius: 15, borderTopRightRadius: 15 }}>
                    <div className="caption">{policyholderState.disabled ?
                        "استعلام"
                        :
                        "مشخصات بیمه گزار/مشتری"
                    }
                    </div>
                </div>

                <div className="box-body form" style={{ border: "1px solid white", borderRadius: 15 }}>
                    <div className="form-body">

                        {policyholderState.disabled && !policyholderState.policyholderData &&
                            <Alert
                                style={{ marginBottom: 20, textAlign: 'justify' }}
                                description={"لطفا اطلاعات را ملاحظه نموده و تکمیل نمایید."}
                                type="info"
                                showIcon
                                icon={<InfoCircleFilled className="blink" />}
                            />
                        }

                        {!policyholderState.disabled && policyholderState.policyholderData &&
                            <Alert
                                style={{ marginBottom: 20, textAlign: 'justify' }}
                                description={"لطفا اطلاعات را ملاحظه نموده و تکمیل نمایید."}
                                type="info"
                                showIcon
                                icon={<InfoCircleFilled className="blink" />}
                            />
                        }

                        {!policyholderState.disabled && !policyholderState.policyholderData &&
                            <Alert
                                style={{ marginBottom: 20, textAlign: 'justify' }}
                                type="warning"
                                icon={<InfoCircleFilled className="blink" />}
                                showIcon
                                description="اطلاعات یافت نشد، لطفا صحت تاریخ تولد و کد ملی را کنترل نموده و در صورت  عدم تطابق با مشخصات شناسنامه، با کلیک بر روی استعلام مجدد، اطلاعات را دوباره استعلام  کنید، در غیر اینصورت، اطلاعات خود را در فرم وارد نموده و ثبت نمایید"
                            />
                        }

                        <Form.Item
                            name="nationalCode"
                            label="کد ملی"
                            rules={[
                                { required: true, message: 'کد ملی الزامی است' },
                                {
                                    validator: () => {
                                        const nationalCode = form.getFieldValue('nationalCode');
                                        if (!Core.isNationalCode(nationalCode))
                                            return Promise.reject(new Error('کد ملی میبایست معتبر باشد'));
                                        return Promise.resolve();
                                    }
                                }]}
                            initialValue={props.nationalCode}
                        >
                            <Input
                                autoComplete="off"
                                placeholder="کد ملی"
                                disabled
                            />
                        </Form.Item>

                        <CustomJalaliDatePicker
                            name="birthDate"
                            label="تاریخ تولد"
                            form={form}
                            rules={[{
                                required: true,
                                message: 'تاریخ تولد الزامی است'
                            }]}
                            defaultValue={policyholderState.birthDate}
                            onChange={value => setPolicyholderState({ ...policyholderState, birthDate: value })}
                            disabled={!policyholderState.disabled || policyholderState.loading}
                        />

                        {!policyholderState.disabled &&
                            <div>

                                <Form.Item
                                    name="firstName"
                                    label="نام"
                                    rules={[{ required: true, message: 'نام الزامی است' }]}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="نام"
                                        disabled={!!policyholderState && policyholderState.policyholderData?.firstName}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="lastName"
                                    label="نام خانوادگی"
                                    rules={[{ required: true, message: 'نام الزامی است' }]}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="نام خانوادگی"
                                        disabled={!!policyholderState && policyholderState.policyholderData?.lastName}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="fatherName"
                                    label="نام پدر"
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="نام پدر"
                                        disabled={!!policyholderState && policyholderState.policyholderData?.fatherName}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="phone"
                                    label="شماره موبایل"
                                    rules={[
                                        { required: true, message: 'شماره موبایل اجباری است' },
                                        { len: 11, message: 'شماره موبایل باید 11 رقم باشد' }
                                    ]}
                                >
                                    <Input
                                        placeholder="شماره موبایل"
                                        type="text"
                                        onKeyPress={(event: any) => {
                                            if (event.charCode < 48 || event.charCode > 57) {
                                                event.preventDefault();
                                            }
                                        }}
                                        maxLength={11}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="province"
                                    label="استان"
                                >
                                    <AutoComplete
                                        allowClear
                                        options={provinceState.provinceList.map(p => { return { key: p.Code, value: p.Name } })}
                                        onSearch={(value) => {
                                            const list = provinceState.originalProvinceList.filter(p => p.Name.indexOf(value) > -1);
                                            setProvinceState({ ...provinceState, provinceList: list });
                                        }}
                                        onChange={(_: any, option: any) => {
                                            const newProvinceState = { ...provinceState, selectedProvinceCode: option?.key };
                                            setProvinceState(newProvinceState);
                                            form.setFields([{ name: 'city', value: undefined }]);
                                            getCityList(option?.key);
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="city"
                                    label="شهر"
                                >
                                    <AutoComplete
                                        options={cityState.cityList.map(p => { return { key: p.Code, value: p.Name } })}
                                        optionFilterProp="name"
                                        onSearch={value => debouncedCitySearch(provinceState.selectedProvinceCode, value)}
                                        onChange={(_: any, option: any) => {
                                            const newCityState = { ...cityState, selectedCityCode: option?.key };
                                            setCityState(newCityState);
                                        }}
                                    >
                                        <Input
                                            suffix={cityState.loading ? <LoadingOutlined style={{ width: 12, height: 12, color: 'rgba(0, 0, 0, 0.25)', fontSize: 12, marginLeft: -5 }} /> : <DownOutlined style={{ width: 12, height: 12, color: 'rgba(0, 0, 0, 0.25)', fontSize: 12, marginLeft: -5 }} />}
                                            allowClear
                                        />
                                    </AutoComplete>
                                </Form.Item>

                                <Form.Item
                                    name="address"
                                    label="آدرس"
                                >
                                    <TextArea
                                        placeholder={"آدرس"}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="postalCode"
                                    label="کد پستی"
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="کد پستی"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="email"
                                    label="ایمیل"
                                    rules={[{ required: true, message: 'ایمیل الزامی است' }]}
                                >
                                    <Input
                                        placeholder={"ایمیل"}
                                        type={"email"}
                                    />
                                </Form.Item>

                            </div>
                        }

                        {(!state.loading && !policyholderState.loading) &&
                            <Captcha
                                showLabel
                                form={form}
                                name="captcha"
                                label="متن تصویر"
                            />
                        }

                        <Form.Item style={{ direction: 'ltr', textAlign: 'justify' }}>
                            {
                                !policyholderState.disabled &&
                                <Button type="primary" htmlType="submit" className="login-form-button" loading={state.loading}>
                                    <Timer key="timer" estimationTime={{ minutes: 15, seconds: 0 }} expired={back} />
                                    <span>ثبت</span>
                                </Button>
                            }
                            {
                                policyholderState.disabled &&
                                <Button type="primary" htmlType="submit" className="login-form-button" loading={policyholderState.loading}>
                                    {policyholderState.loading ? <span>در حال استعلام</span> : <span>استعلام</span>}
                                </Button>
                            }
                            <Button
                                type="default"
                                className="login-form-button"
                                htmlType="button"
                                onClick={() => {
                                    if (state.loading || policyholderState.loading) {
                                        form.resetFields(['birthDate']);
                                        setPolicyholderState({ disabled: true, loading: false });
                                    }
                                    else {
                                        back();
                                    }
                                }}
                            >
                                {state.loading || policyholderState.loading ? <span>لغو</span> : <span>برگشت</span>}
                            </Button>
                            {
                                !policyholderState.disabled &&
                                <Button type="default" htmlType="button" className="login-form-button" loading={state.loading}
                                    onClick={() => {
                                        form.resetFields(['birthDate']);
                                        setPolicyholderState({ disabled: true, loading: false });
                                    }}
                                >
                                    استعلام مجدد
                                </Button>
                            }
                        </Form.Item>


                    </div>
                </div>
            </div>

        </Form>
    );
};

export default RegisterPolicyholder;